import { useEffect, useRef, useState } from 'react'

import BottomSheet from '../../../components/BottomSheet'

/**
 * Bottom-sheet number pad for high-gross scores (rare overflow beyond
 * par+3). Triggered by the "..." cell at the end of the standard 6
 * score buttons. Captures the value cleanly without breaking the
 * fixed-grid visual rhythm of the per-player card.
 *
 * Slide animation + Escape-to-close + backdrop tap + body scroll lock
 * all live in `BottomSheet`. Focus is restored to the input on open
 * via the same RAF-deferred approach as StackEditSheet — the sheet's
 * purpose IS this input, autofocus is the whole point.
 */
const HighScoreSheet = ({
  open,
  par,
  initial,
  onClose,
  onSave,
}: {
  open: boolean
  par: number
  initial: number | null
  onClose: () => void
  onSave: (gross: number) => void
}) => {
  const [value, setValue] = useState<string>(
    initial != null ? String(initial) : ''
  )
  const inputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (open) setValue(initial != null ? String(initial) : '')
  }, [open, initial])
  useEffect(() => {
    if (!open) return undefined
    const id = window.requestAnimationFrame(() => inputRef.current?.focus())
    return () => window.cancelAnimationFrame(id)
  }, [open])

  const parsed = parseInt(value, 10)
  const valid = !Number.isNaN(parsed) && parsed > par + 3 && parsed <= 20

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      ariaLabel="Høyere score"
      backdropClassName="bg-ink-900/45 backdrop-blur-sm"
      panelClassName="max-w-xl bg-paper"
      showGrabHandle
    >
      <div
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        }}
      >
        <div className="px-6 pt-3 pb-4">
          <h2 className="font-display font-extrabold text-lg text-green-900">
            Høyere score
          </h2>
          <p className="text-xs text-ink-700 mt-1">
            Knappene over dekker opp til {par + 3}. For høyere score, skriv inn
            brutto antall slag på dette hullet.
          </p>
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            min={par + 4}
            max={20}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={String(par + 4)}
            className="mt-3 w-full px-4 py-3 bg-cream-card rounded-lg text-lg font-mono num focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
        <div className="px-6 flex flex-col gap-2">
          <button
            type="button"
            disabled={!valid}
            onClick={() => valid && onSave(parsed)}
            className="w-full py-3 rounded-xl font-display font-extrabold text-base bg-green-700 hover:bg-green-800 text-paper transition-colors disabled:opacity-50"
            style={{ boxShadow: '0 8px 20px -8px rgba(11, 42, 28, 0.6)' }}
          >
            Lagre score
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl font-semibold text-sm bg-cream-card text-ink-700 hover:bg-ink-100 transition-colors"
          >
            Avbryt
          </button>
        </div>
      </div>
    </BottomSheet>
  )
}

export default HighScoreSheet
